import { Globe, Smartphone, Database, Brain, Palette } from "lucide-react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { fadeUp, staggerContainer } from "@/lib/animations";

interface Service {
    num: string;
    icon: React.ReactNode;
    key: string;
    tags?: string[];
}

const SERVICES: Service[] = [
    {
        num: "01",
        icon: <Globe className="w-5 h-5" />,
        key: "web",
        tags: ["NEXT.JS", "REACT", "TYPESCRIPT", "POSTGRES", "PRISMA", "REDIS", "TRPC"],
    },
    {
        num: "02",
        icon: <Smartphone className="w-5 h-5" />,
        key: "mobile",
        tags: ["FLUTTER", "REACT NATIVE", "SWIFT", "KOTLIN", "EXPO", "FIREBASE"],
    },
    {
        num: "03",
        icon: <Database className="w-5 h-5" />,
        key: "erp",
        tags: ["ERP", "CRM", "POS", "METABASE", "KAFKA", "ETL", "OLAP"],
    },
    {
        num: "04",
        icon: <Brain className="w-5 h-5" />,
        key: "ai",
        tags: ["OPENAI", "CLAUDE", "RAG", "EMBEDDINGS", "MCP", "VECTOR DB", "AGENTS"],
    },
    {
        num: "05",
        icon: <Palette className="w-5 h-5" />,
        key: "branding",
        tags: ["FIGMA", "MOTION", "3D", "PROTOTYPING", "DESIGN SYSTEM"],
    },
];

const ServiceTile = ({ service }: { service: Service }) => {
    const { t } = useTranslation();

    return (
        <motion.article
            variants={fadeUp}
            className="group relative border border-white/10 hover:border-white/25 transition-colors duration-300 p-6 sm:p-7 md:p-8 flex flex-col min-h-[280px] sm:min-h-[320px] bg-white/[0.015]"
        >
            {/* Top edge neon stroke — animates in on hover */}
            <span
                aria-hidden
                className="absolute top-0 inset-x-0 h-px scale-x-0 group-hover:scale-x-100 origin-start transition-transform duration-500"
                style={{ background: "var(--gradient-accent-v2)" }}
            />

            <div className="flex items-start justify-between mb-6 sm:mb-8">
                <span className="font-v2-display text-5xl sm:text-6xl md:text-7xl text-white/[0.08] leading-none tabular-nums select-none transition-colors duration-500 group-hover:text-white/15">
                    [{service.num}]
                </span>
                <span className="text-white/55 transition-colors duration-300 group-hover:text-white">
                    {service.icon}
                </span>
            </div>

            <h3 className="font-v2-display text-xl sm:text-2xl text-white leading-tight mb-3">
                {t(`services.items.${service.key}.title`)}
            </h3>

            <p className="text-sm text-white/60 leading-relaxed font-medium mb-6 line-clamp-4 flex-1">
                {t(`services.items.${service.key}.desc`)}
            </p>

            {service.tags && service.tags.length > 0 && (
                <div className="flex flex-wrap gap-1.5 pt-4 border-t border-white/8">
                    {service.tags.map((tag) => (
                        <span
                            key={tag}
                            className="font-mono-brutal text-[9px] px-2 py-1 border border-white/10 text-white/55 transition-colors duration-300 group-hover:border-white/20 group-hover:text-white/75"
                        >
                            {tag}
                        </span>
                    ))}
                </div>
            )}
        </motion.article>
    );
};

const ServicesTimeline = () => {
    const { t } = useTranslation();

    return (
        <section
            id="services"
            className="py-12 sm:py-16 md:py-24 lg:py-28"
            aria-labelledby="services-heading"
        >
            <div className="container mx-auto px-5 sm:px-6 mb-10 sm:mb-14">
                <div className="flex items-center gap-3 mb-6 sm:mb-8">
                    <span aria-hidden className="brutal-glyph" style={{ height: "0.65em" }} />
                    <span className="font-mono-brutal text-[11px] text-white/60">
                        {t("services.eyebrow")}
                    </span>
                    <span aria-hidden className="flex-1 h-px bg-white/10" />
                    <span className="font-mono-brutal text-[10px] text-white/35 tabular-nums">
                        [{String(SERVICES.length).padStart(2, "0")} SERVICES]
                    </span>
                </div>

                <h2
                    id="services-heading"
                    className="font-v2-display text-white text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.95] max-w-4xl"
                >
                    {t("services.title_prefix")}{" "}
                    <span
                        style={{
                            backgroundImage: "var(--gradient-accent-v2)",
                            WebkitBackgroundClip: "text",
                            backgroundClip: "text",
                            color: "transparent",
                        }}
                    >
                        {t("services.title_highlight")}
                    </span>
                </h2>
                <p className="mt-5 sm:mt-6 text-sm sm:text-base md:text-lg text-white/60 leading-relaxed max-w-2xl">
                    {t("services.subtitle")}
                </p>
            </div>

            <div className="container mx-auto px-5 sm:px-6">
                <motion.div
                    variants={staggerContainer(0.08, 0.05)}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, amount: 0.1 }}
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/8"
                >
                    {SERVICES.map((service) => (
                        <ServiceTile key={service.num} service={service} />
                    ))}
                    {/* Filler tile to keep the brutalist grid lines intact when count
                        doesn't divide the column count evenly. Hidden on single-col. */}
                    <div className="hidden sm:flex lg:hidden border border-white/10 p-6 sm:p-7 bg-background flex-col justify-end">
                        <span className="font-v2-display text-3xl text-white/[0.08]">
                            [TRBD]
                        </span>
                    </div>
                    <div className="hidden lg:flex border border-white/10 p-6 sm:p-7 bg-background flex-col justify-end">
                        <span className="font-v2-display text-3xl text-white/[0.08]">
                            [TRBD]
                        </span>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default ServicesTimeline;
